import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../contexts/AuthContext';
import { isAdminOrSuperAdmin } from '../utils/auth';
import api from '../services/api';
import LoadingSpinner from '../components/LoadingSpinner';
import '../App.css';

interface Team {
  id: string;
  name: string;
  teamNumber: string;
}

const formatOptions = [
  { value: 'single_elimination', label: 'Single Elimination' },
  { value: 'double_elimination', label: 'Double Elimination' },
  { value: 'round_robin', label: 'Round Robin' },
  { value: 'group_knockout', label: 'Group + Knockout' },
];

const CreateTournament: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [name, setName] = useState('');
  const [format, setFormat] = useState('single_elimination');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [groups, setGroups] = useState(4);
  const [availableTeams, setAvailableTeams] = useState<Team[]>([]);
  const [selectedTeamIds, setSelectedTeamIds] = useState<string[]>([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    const fetchTeams = async () => {
      try {
        const res = await api.get('/teams', { params: { limit: '100' } });
        setAvailableTeams(res.data.teams || []);
      } catch (err) {
        console.error('Error fetching teams:', err);
      } finally {
        setLoading(false);
      }
    };
    fetchTeams();
  }, []);

  if (!isAdminOrSuperAdmin(user?.profileType)) {
    return <div className="page-container"><p>Admin access required.</p></div>;
  }

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError('');

    if (!name.trim()) {
      setError('Tournament name is required');
      return;
    }

    if (selectedTeamIds.length < 2) {
      setError('At least 2 teams are required');
      return;
    }

    setSubmitting(true);
    try {
      const settings: Record<string, any> = {};
      if (format === 'group_knockout') {
        settings.groups = groups;
      }

      const res = await api.post('/tournaments', {
        name: name.trim(),
        format,
        startDate: startDate || undefined,
        endDate: endDate || undefined,
        settings,
      });

      const tournamentId = res.data.id;

      // Add teams
      for (let i = 0; i < selectedTeamIds.length; i++) {
        await api.post(`/tournaments/${tournamentId}/teams`, {
          teamId: selectedTeamIds[i],
          seed: i + 1,
        });
      }

      navigate(`/tournaments/${tournamentId}/manage`);
    } catch (err: any) {
      setError(err.response?.data?.error || 'Failed to create tournament');
    } finally {
      setSubmitting(false);
    }
  };

  const toggleTeam = (teamId: string) => {
    setSelectedTeamIds(prev =>
      prev.includes(teamId)
        ? prev.filter(id => id !== teamId)
        : [...prev, teamId]
    );
  };

  if (loading) return <LoadingSpinner fullPage />;

  return (
    <div className="page-container" style={{ maxWidth: '700px', margin: '0 auto' }}>
      <h1>Create Tournament</h1>

      {error && <div className="alert alert-danger">{error}</div>}

      <form onSubmit={handleSubmit}>
        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Tournament Name *</label>
          <input
            type="text"
            value={name}
            onChange={e => setName(e.target.value)}
            placeholder="e.g. Spring Cup 2026"
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
          />
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Format *</label>
          <select
            value={format}
            onChange={e => setFormat(e.target.value)}
            style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
          >
            {formatOptions.map(opt => (
              <option key={opt.value} value={opt.value}>{opt.label}</option>
            ))}
          </select>
        </div>

        {format === 'group_knockout' && (
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Number of Groups</label>
            <input
              type="number"
              min={2}
              max={8}
              value={groups}
              onChange={e => setGroups(parseInt(e.target.value) || 4)}
              style={{ width: '120px', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
            />
          </div>
        )}

        <div style={{ display: 'flex', gap: '16px', marginBottom: '16px' }}>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>Start Date</label>
            <input
              type="date"
              value={startDate}
              onChange={e => setStartDate(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
            />
          </div>
          <div style={{ flex: 1 }}>
            <label style={{ display: 'block', fontWeight: 600, marginBottom: '4px' }}>End Date</label>
            <input
              type="date"
              value={endDate}
              onChange={e => setEndDate(e.target.value)}
              style={{ width: '100%', padding: '8px 12px', borderRadius: '6px', border: '1px solid #dee2e6' }}
            />
          </div>
        </div>

        <div style={{ marginBottom: '16px' }}>
          <label style={{ display: 'block', fontWeight: 600, marginBottom: '8px' }}>
            Select Teams ({selectedTeamIds.length} selected)
          </label>
          <div style={{ maxHeight: '300px', overflow: 'auto', border: '1px solid #dee2e6', borderRadius: '6px', padding: '8px' }}>
            {availableTeams.map(team => (
              <label
                key={team.id}
                style={{
                  display: 'flex', alignItems: 'center', gap: '8px', padding: '6px 8px',
                  cursor: 'pointer', borderRadius: '4px',
                  backgroundColor: selectedTeamIds.includes(team.id) ? '#e8f0fe' : 'transparent'
                }}
              >
                <input
                  type="checkbox"
                  checked={selectedTeamIds.includes(team.id)}
                  onChange={() => toggleTeam(team.id)}
                />
                <span>{team.name}</span>
                <span style={{ color: '#6c757d', fontSize: '13px' }}>#{team.teamNumber}</span>
              </label>
            ))}
            {availableTeams.length === 0 && (
              <p style={{ color: '#6c757d', textAlign: 'center', padding: '12px' }}>No teams available</p>
            )}
          </div>
        </div>

        <div style={{ display: 'flex', gap: '12px' }}>
          <button type="submit" className="btn btn-primary" disabled={submitting}>
            {submitting ? 'Creating...' : 'Create Tournament'}
          </button>
          <button type="button" className="btn btn-outline-secondary" onClick={() => navigate('/tournaments')}>
            Cancel
          </button>
        </div>
      </form>
    </div>
  );
};

export default CreateTournament;
